import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * VacancyWidget
 * - Полностью изолированные стили с помощью Shadow DOM.
 * - Оптимизация фильтрации через useMemo.
 * - Улучшенный UX: фиксированные фильтры и правильно работающий скролл контента.
 * - Адаптивный дизайн для мобильных устройств.
 */

// --------- ДАННЫЕ ПО УМОЛЧАНИЮ (O'zbek tilida) ---------
const defaultVacancies = [
  {
    id: 6,
    title: "Professor vazifasini bajaruvchi",
    department: "Iqtisodiyot kafedrasi",
    employmentType: "To‘liq bandlik",
    description:
      "Iqtisodiyot kafedrasida professor vazifasini bajaruvchi sifatida dars mashg‘ulotlarini olib borish.",
    postedDate: "2025-06-29",
  },
  {
    id: 7,
    title: "Dotsent vazifasini bajaruvchi",
    department: "Iqtisodiyot kafedrasi",
    employmentType: "To‘liq bandlik",
    description:
      "Iqtisodiyot kafedrasida dotsent vazifasini bajaruvchi sifatida o‘quv va ilmiy ishlarni amalga oshirish.",
    postedDate: "2025-06-29",
  },
  {
    id: 8,
    title: "Professor vazifasini bajaruvchi",
    department: "Jahon tillari kafedrasi",
    employmentType: "To‘liq bandlik",
    description:
      "Jahon tillari kafedrasida professor vazifasini bajaruvchi sifatida dars mashg‘ulotlarini olib borish.",
    postedDate: "2025-09-03",
  },
  {
    id: 9,
    title: "Katta o'qituvchi",
    department: "Jahon tillari kafedrasi",
    employmentType: "Yarim stavka",
    description: "Jahon tillari kafedrasida amaliy mashg'ulotlar o'tkazish.",
    postedDate: "2025-09-01",
  },
  {
    id: 10,
    title: "Laborant",
    department: "Kimyo kafedrasi",
    employmentType: "To‘liq bandlik",
    description: "Kimyo laboratoriyasida tajribalarni tayyorlash va o'tkazish.",
    postedDate: "2025-08-25",
  },
];

const defaultFilters = {
  department: "Barcha kafedralar",
  employmentType: "Har qanday bandlik",
};

// --------- ИКОНКИ ---------
const NoResultsIcon = () => (
  <svg
    width="60"
    height="60"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M19.25 19.25 15.5 15.5M4.75 12A7.25 7.25 0 0 1 12 4.75m0 14.5a7.25 7.25 0 0 0 0-14.5m0 14.5A7.25 7.25 0 0 1 4.75 12m7.25 7.25a7.25 7.25 0 0 0 7.25-7.25"
    ></path>
  </svg>
);
const BackIcon = () => (
  <svg
    className="icon"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    ></path>
  </svg>
);

// --------- ОБНОВЛЁННЫЕ И ИСПРАВЛЕННЫЕ СТИЛИ ---------
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  :host {
    --orange-glow: #ff984e;
    --orange-vibrant: #f97316;
    --text-header: #1a202c;
    --text-body: #4a5568;
    --text-muted: #718096;
    --border-color: #e2e8f0;
    --bg-main: #f7fafc;
    --bg-card: #ffffff;
    --bg-filters: rgba(255, 255, 255, 0.65);
    --backdrop-blur: blur(12px);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: var(--text-body);
    box-sizing: border-box;
  }
  *, *:before, *:after { box-sizing: inherit; }

  .vacancy-root {
    background-color: var(--bg-main);
    background-image: radial-gradient(circle at 10% 10%, #fde68a1a, transparent),
                      radial-gradient(circle at 80% 90%, #f973161a, transparent);
  }

  /* 1. Основной контейнер занимает всю высоту окна */
  .vacancy-page-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
    height: 100vh; /* Фоллбэк для старых браузеров */
    height: 100dvh; /* Динамическая высота окна, учитывает интерфейс мобильных браузеров */
    display: flex;
    flex-direction: column;
  }
  
  /* Хедер имеет фиксированную высоту */
  .header-wrapper {
    padding-top: 40px;
    flex-shrink: 0; /* Запрещаем сжиматься */
  }

  .back-button {
    display: inline-flex; align-items: center; gap: 8px;
    margin-bottom: 24px; padding: 10px 20px; font-size: 0.9rem;
    font-weight: 600; color: var(--text-body);
    background-color: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 10px; cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .back-button:hover { color: #ffffff; background-color: var(--orange-vibrant); border-color: var(--orange-vibrant); transform: translateY(-3px); box-shadow: 0 8px 20px -5px rgba(249, 115, 22, 0.4); }
  .back-button .icon { width: 18px; height: 18px; transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
  .back-button:hover .icon { transform: translateX(-3px); }

  .page-header { text-align: center; margin: 0 0 30px 0; color: var(--text-header); font-size: 2.8rem; font-weight: 800; letter-spacing: -1.5px; }
  .page-header span { color: var(--orange-vibrant); }

  /* 2. Основной макет занимает ВСЁ оставшееся пространство по высоте */
  .vacancy-layout {
    display: flex;
    gap: 40px;
    flex-grow: 1; /* Растягиваемся на доступную высоту */
    overflow: hidden; /* Важно: предотвращает выход контента за пределы и появление двойного скролла */
  }

  /* Фильтры имеют фиксированную ширину */
  .filters-panel {
    width: 320px;
    flex-shrink: 0;
    background: var(--bg-filters);
    backdrop-filter: var(--backdrop-blur);
    -webkit-backdrop-filter: var(--backdrop-blur);
    border: 1px solid rgba(255,255,255,0.7);
    border-radius: 20px;
    padding: 28px;
    box-shadow: 0 8px 32px 0 rgba(100,116,139,0.12);
  }
  .filters-header { font-size: 1.3rem; font-weight: 700; color: var(--text-header); margin: 0 0 24px 0; }
  .filter-group { margin-bottom: 22px; }
  .filter-group label { display: block; font-weight: 600; font-size: 0.9rem; color: var(--text-body); margin-bottom: 10px; }
  .filter-input { width: 100%; padding: 12px 16px; font-size: 1rem; border-radius: 10px; border: 1px solid var(--border-color); background-color: rgba(255,255,255,0.8); transition: all 0.3s ease; appearance: none; background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23718096%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E'); background-repeat: no-repeat; background-position: right 1rem center; background-size: .65em auto; padding-right: 2.5rem; }
  .filter-input:focus { outline: none; border-color: var(--orange-glow); box-shadow: 0 0 0 4px rgba(249,115,22,0.15); }
  .reset-button { width: 100%; padding: 12px; font-size: 0.95rem; font-weight: 600; border: 1px solid transparent; color: #fff; background: linear-gradient(45deg, var(--orange-vibrant), var(--orange-glow)); border-radius: 10px; cursor: pointer; transition: all 0.3s ease; margin-top: 10px; }
  .reset-button:hover { transform: translateY(-2px); box-shadow: 0 6px 15px rgba(249,115,22,0.3); }

  /* 3. Список вакансий растягивается и получает собственную прокрутку */
  .vacancy-list {
    flex-grow: 1; /* Растягиваемся на доступную ширину */
    overflow-y: auto; /* Включаем вертикальный скролл ТОЛЬКО для этого блока */
    padding: 5px 20px 40px 5px;
    align-content: start;
    display: grid;
    gap: 30px;
  }
  .vacancy-list::-webkit-scrollbar { width: 8px; }
  .vacancy-list::-webkit-scrollbar-track { background: transparent; }
  .vacancy-list::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 10px; }
  .vacancy-list::-webkit-scrollbar-thumb:hover { background-color: var(--text-muted); }

  @keyframes card-fade-in { from { opacity: 0; transform: translateY(25px); } to { opacity: 1; transform: translateY(0); } }
  .vacancy-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 18px; transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1); opacity: 0; animation: card-fade-in 0.6s ease-out forwards; padding: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03); }
  .vacancy-card:hover { transform: translateY(-10px); border-color: var(--orange-glow); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.08), 0 10px 10px -5px rgba(0,0,0,0.04); }
  .card-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px; }
  .card-title { font-size: 1.6rem; font-weight: 700; color: var(--text-header); margin: 0; transition: color 0.3s ease; }
  .vacancy-card:hover .card-title { color: var(--orange-vibrant); }
  .card-date { font-size: 0.85rem; color: var(--text-muted); white-space: nowrap; font-weight: 500; padding-top: 5px; }
  .card-description { color: var(--text-body); line-height: 1.7; margin: 0 0 24px 0; }
  .card-tags { display: flex; flex-wrap: wrap; gap: 12px; }
  .tag { display: inline-flex; align-items: center; padding: 8px 14px; font-size: 0.8rem; font-weight: 600; border-radius: 20px; background-color: #f3f4f6; color: var(--text-body); transition: all .3s ease; }
  .vacancy-card:hover .tag { background-color: #fff7ed; color: var(--orange-vibrant); }

  .no-vacancies { text-align: center; padding: 80px 40px; color: var(--text-muted); grid-column: 1 / -1; }
  .no-vacancies h2 { color: var(--text-header); font-weight: 700; font-size: 1.5rem; margin: 20px 0 8px 0; }

  /* Адаптивность: на мобильных отключаем сложный макет и возвращаем обычный поток */
  @media (max-width: 1024px) {
    .vacancy-page-container {
        height: auto; /* Отключаем фиксацию высоты */
        padding: 0 16px;
    }
    .header-wrapper { padding-top: 20px; }
    .page-header { font-size: 2.2rem; }
    .vacancy-layout {
        flex-direction: column;
        overflow: visible; /* Возвращаем обычное поведение */
    }
    .filters-panel {
        width: 100%;
        margin-bottom: 30px;
    }
    .vacancy-list {
        overflow-y: visible; /* Отключаем внутренний скролл */
        padding: 0 0 40px 0;
    }
  }
`;

// --------- ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ ---------
// (Без изменений)
function ShadowRootContainer({ styleText, children }) {
  const hostRef = useRef(null);
  const shadowRef = useRef(null);

  if (!shadowRef.current && typeof document !== "undefined") {
    const host = document.createElement("div");
    host.className = "vacancy-shadow-host";
    shadowRef.current = host.attachShadow({ mode: "open" });
  }

  useEffect(() => {
    if (!hostRef.current) return;
    const hostNode = hostRef.current;
    const shadowHost = shadowRef.current?.host;
    if (shadowHost) {
      hostNode.appendChild(shadowHost);
    }
    return () => {
      if (hostNode && shadowHost) {
        // Проверяем, существует ли еще узел перед удалением
        if (hostNode.contains(shadowHost)) {
          hostNode.removeChild(shadowHost);
        }
      }
    };
  }, []);

  return (
    <div ref={hostRef}>
      {shadowRef.current
        ? createPortal(
            <div className="vacancy-root">
              <style>{styleText}</style>
              {children}
            </div>,
            shadowRef.current
          )
        : null}
    </div>
  );
}

// --------- ОСНОВНОЙ ВИДЖЕТ ---------
// (Без изменений в логике)
export default function VacancyWidget({ initialData = defaultVacancies }) {
  const [vacancies] = useState(initialData);
  const [filters, setFilters] = useState(defaultFilters);

  const departments = useMemo(
    () => [
      "Barcha kafedralar",
      ...Array.from(new Set(vacancies.map((v) => v.department))),
    ],
    [vacancies]
  );
  const employmentTypes = useMemo(
    () => [
      "Har qanday bandlik",
      ...Array.from(new Set(vacancies.map((v) => v.employmentType))),
    ],
    [vacancies]
  );

  const filteredVacancies = useMemo(() => {
    return vacancies.filter((v) => {
      const matchesDep =
        filters.department === "Barcha kafedralar" ||
        v.department === filters.department;
      const matchesType =
        filters.employmentType === "Har qanday bandlik" ||
        v.employmentType === filters.employmentType;
      return matchesDep && matchesType;
    });
  }, [vacancies, filters]);

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString("uz-UZ", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleGoBack = () => {
    if (typeof window !== "undefined") window.history.back();
  };

  return (
    <ShadowRootContainer styleText={styles}>
      <div
        className="vacancy-page-container"
        role="region"
        aria-label="Universitet vakansiyalari"
      >
        <div className="header-wrapper">
          <button
            onClick={handleGoBack}
            className="back-button"
            type="button"
            aria-label="Orqaga qaytish"
          >
            <BackIcon />
            <span>Orqaga</span>
          </button>

          <h1 className="page-header">
            Universitet <span>vakansiyalari</span>
          </h1>
        </div>

        <div className="vacancy-layout">
          <aside className="filters-panel" aria-labelledby="filters-heading">
            <h2 id="filters-heading" className="filters-header">
              Filterlar
            </h2>

            <div className="filter-group">
              <label htmlFor="vacancy-department">Bo'lim yoki kafedra</label>
              <select
                id="vacancy-department"
                name="department"
                className="filter-input"
                value={filters.department}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, department: e.target.value }))
                }
              >
                {departments.map((dep) => (
                  <option key={dep} value={dep}>
                    {dep}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="vacancy-employmentType">Bandlik turi</label>
              <select
                id="vacancy-employmentType"
                name="employmentType"
                className="filter-input"
                value={filters.employmentType}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, employmentType: e.target.value }))
                }
              >
                {employmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="reset-button"
              type="button"
              onClick={() => {
                setFilters(defaultFilters);
              }}
            >
              Filtrlarni tozalash
            </button>
          </aside>

          <main className="vacancy-list" aria-live="polite">
            {filteredVacancies.length > 0 ? (
              filteredVacancies.map((vacancy, index) => (
                <article
                  key={vacancy.id}
                  className="vacancy-card"
                  style={{ animationDelay: `${index * 100}ms` }}
                  aria-labelledby={`vacancy-title-${vacancy.id}`}
                >
                  <div className="card-header">
                    <h3
                      id={`vacancy-title-${vacancy.id}`}
                      className="card-title"
                    >
                      {vacancy.title}
                    </h3>
                    <div className="card-date">
                      {formatDate(vacancy.postedDate)}
                    </div>
                  </div>
                  <p className="card-description">{vacancy.description}</p>
                  <div className="card-tags">
                    <span className="tag">{vacancy.department}</span>
                    <span className="tag">{vacancy.employmentType}</span>
                  </div>
                </article>
              ))
            ) : (
              <div className="no-vacancies" role="status">
                <NoResultsIcon />
                <h2>Vakansiyalar topilmadi</h2>
                <p>Iltimos, boshqa filtrlarni tanlang.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </ShadowRootContainer>
  );
}

//Baribir Lox//
//Polniy Lox//